import { Outlet, useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import { RegistrationBreadcrumb } from "./components/registration-breadcrumb";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Edit2, Search, RefreshCw, PlusCircle, ChevronLeft } from "lucide-react";
import { useState, useRef, useCallback } from "react";
import type { RegistrationLayoutContext } from "./hooks/useRegistrationLayout";

declare global {
  interface Window {
    navigation?: {
      canGoForward: boolean;
      canGoBack: boolean;
    };
  }
}

/**
 * Shared layout for all registration pages.
 * This component stays mounted across page transitions within /dash/registrations/*
 * which allows the breadcrumb animations to work smoothly.
 */
export function RegistrationLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "all";

  // Shared state for list page
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const refetchRef = useRef<(() => Promise<void>) | null>(null);

  const registerRefetch = useCallback((fn: () => Promise<void>) => {
    refetchRef.current = fn;
  }, []);

  const triggerRefetch = useCallback(async () => {
    if (refetchRef.current) {
      await refetchRef.current();
    }
  }, []);

  // Determine page type for action buttons
  const path = location.pathname;
  const isListPage = path === "/dash/registrations";
  const isDetailsPage = path.match(/^\/dash\/registrations\/[^/]+$/) && !path.endsWith("/new");
  const isEditPage = path.match(/^\/dash\/registrations\/[^/]+\/edit$/);
  const isNewPage = path === "/dash/registrations/new";
  const registrationId = params.id;

  // Determine back button destination
  const getBackDestination = () => {
    if (isEditPage && registrationId) {
      return `/dash/registrations/${registrationId}?tab=${activeTab}`;
    }
    if (isDetailsPage || isNewPage) {
      return `/dash/registrations?tab=${activeTab}`;
    }
    return null;
  };

  // const getForwardDestination = () => {
  //   if ((isDetailsPage || isNewPage) && registrationId) {
  //     return `/dash/registrations/${registrationId}/edit?tab=${activeTab}`;
  //   }
  //   return null;
  // };

  const backDestination = getBackDestination();
  // const forwardDestination = getForwardDestination();

  const contextValue: RegistrationLayoutContext = {
    searchQuery,
    setSearchQuery,
    registerRefetch,
    triggerRefetch,
    isLoading,
    setIsLoading,
  };

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Fixed height header to prevent layout shift between pages */}
      <header className="flex h-[72px] items-center border-b border-gray-200 bg-white px-6">
        <div className="flex w-full items-center justify-between">
          <div className="flex items-center gap-2">
            {/* Back button - only visible on non-list pages */}
            <Button
              variant="ghost"
              size="icon"
              className={`size-8 overflow-hidden text-gray-400 transition-all duration-300 ease-in-out ${(!window.navigation || window.navigation.canGoBack) && !isListPage ? "w-8 opacity-100" : "w-0 opacity-0"}`}
              onClick={() => navigate(-1)}
              tabIndex={backDestination ? 0 : -1}
            >
              <ChevronLeft className="size-5 translate-y-px shrink-0" />
            </Button>

            {/* Forward button - only visible on non-list pages */}
            {/* <Button
              variant="ghost"
              size="icon"
              className={`size-8 overflow-hidden text-muted-foreground transition-all duration-300 ease-in-out ${(!window.navigation || window.navigation.canGoForward) && !isListPage ? "w-8 opacity-100" : "w-0 opacity-0"}`}
              onClick={() => navigate(1)}
              tabIndex={forwardDestination ? 0 : -1}
            >
              <ChevronRight className="h-5 w-5 shrink-0" />
            </Button> */}

            {/* Breadcrumb */}
            <RegistrationBreadcrumb />
          </div>

          {/* List page: search + buttons */}
          {isListPage && (
            <div className="flex items-center space-x-4">
              <div className="relative w-50 md:w-70 lg:w-100">
                <Search className="absolute top-1/2 left-3 -translate-y-1/2 transform text-gray-400" size={18} />
                <Input
                  placeholder="Rechercher une personne ou une inscription..."
                  className="w-full rounded-lg border border-gray-300 py-2 pr-4 pl-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <Button
                variant="outline"
                size="icon"
                className="w-10"
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  const svg = (e.currentTarget as HTMLElement).querySelector("svg");
                  svg?.classList.add("animate-spin");
                  triggerRefetch().finally(() => svg?.classList.remove("animate-spin"));
                }}
              >
                <RefreshCw size={18} className={isLoading ? "animate-spin" : ""} />
              </Button>
              <Button onClick={() => navigate("/dash/registrations/new")}>
                <PlusCircle size={18} className="mr-0 md:mr-2" />
                <span className="hidden md:block">Nouvelle Inscription</span>
              </Button>
            </div>
          )}

          {/* Details page: edit button */}
          {isDetailsPage && registrationId && (
            <Button onClick={() => navigate(`/dash/registrations/${registrationId}/edit?tab=${activeTab}`)} className="flex items-center gap-2">
              <Edit2 className="mr-2 h-4 w-4" />
              <span className="hidden md:block">Modifier l'inscription</span>
            </Button>
          )}

          {/* Empty placeholder to maintain header height on new/edit pages */}
          {!isListPage && !isDetailsPage && <div className="h-10" />}
        </div>
      </header>
      <div className="flex-1 overflow-y-auto">
        <Outlet context={contextValue} />
      </div>
    </div>
  );
}
